/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'

import store from '@/store/store.js'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    // Landing Page
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/landing/views/Landing.vue'),
      meta: {
        rule: 'user',
        authRequired: false,
      },
    },
    {
      path: '/about-us',
      name: 'about-us',
      component: () => import('@/views/landing/views/AboutUs.vue'),
      meta: {
        rule: 'user',
        authRequired: false,
      }
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/landing/views/Contact.vue'),
      meta: {
        rule: 'user',
        authRequired: false,
      }
    },
    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================

        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('./views/DashboardAnalytics.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Strona główna',
          }
        },
        {
          path: '/trainings',
          name: 'user_trainings',
          component: () => import('./views/Trainings.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Szkolenia',
          }
        },
        {
          path: '/trainings/:uuid',
          name: 'user_trainings_single',
          component: () => import('./views/TrainingsSingle.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Szkolenie',
          }
        },
        {
          path: '/trainings/:uuid/:element_uuid',
          name: 'training_board',
          component: () => import('./views/TrainingBoard.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Szkolenie',
          }
        },
        {
          path: '/certs',
          name: 'user_certs',
          component: () => import('./views/Certs.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Certs',
          }
        },
        {
          path: '/my-account',
          name: 'my-account',
          component: () => import('./views/MyAccount.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Moje konto',
            breadcrumb: [
              {
                title: 'Strona główna',
                url: '/'
              },
              {
                title: 'Moje konto',
                disabled: true,
              },
            ],
          }
        },
        {
          path: '/help',
          name: 'help',
          component: () => import('./views/Help.vue'),
          meta: {
            rule: 'user',
            authRequired: true,
            pageTitle: 'Help',
          }
        },
        {
          path: '/administration/agreements',
          name: 'agreements',
          component: () => import('./views/administration/Agreements'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Zgody',
            breadcrumb: [
              {
                title: 'Administracja',
                disabled: true,
              },
              {
                title: 'Zgody',
                disabled: true,
              },
            ],
          }
        },
        {
          path: '/administration/agreements/:uuid',
          name: 'agreements_single',
          component: () => import('./views/administration/AgreementsSingle'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Zgoda'
          }
        },
        {
          path: '/administration/settings',
          name: 'settings',
          component: () => import('./views/administration/Settings'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Ustawienia'
          }
        },
        {
          path: '/administration/users',
          name: 'users',
          component: () => import('./views/administration/Users'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Użytkownicy'
          }
        },
        {
          path: '/administration/users/:uuid',
          name: 'users_single',
          component: () => import('./views/administration/UsersSingle'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Użytkownik'
          }
        },
        {
          path: '/administration/trainings',
          name: 'trainings',
          component: () => import('./views/administration/Trainings'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Szkolenia'
          }
        },
        {
          path: '/administration/trainings/:uuid',
          name: 'trainings_single',
          component: () => import('./views/administration/TrainingsSingle'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Szkolenie'
          }
        },
        {
          path: '/administration/lessons',
          name: 'lessons',
          component: () => import('./views/administration/Lessons'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Lekcje'
          }
        },
        {
          path: '/administration/lessons/:uuid',
          name: 'lessons_single',
          component: () => import('./views/administration/LessonsSingle'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Lekcja'
          }
        },
        {
          path: '/administration/exams',
          name: 'exams',
          component: () => import('./views/administration/Exams'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Ankiety i egzaminy'
          }
        },
        {
          path: '/administration/exams/:uuid',
          name: 'exams_single',
          component: () => import('./views/administration/Exam'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Egzamin'
          }
        },
        {
          path: '/administration/issues',
          name: 'issues',
          component: () => import('./views/administration/Issues'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Zgłoszenia'
          }
        },
        {
          path: '/administration/reports',
          name: 'reports',
          component: () => import('./views/administration/Reports'),
          meta: {
            rule: 'admin',
            authRequired: true,
            pageTitle: 'Raporty'
          }
        },
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/pages/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/register',
          name: 'page-register',
          component: () => import('@/views/pages/register/Register.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/password/reset',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'user'
          }
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'user'
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

router.beforeEach((to, from, next) => {
// If auth required, check login. If login fails redirect to login page
  if (to.meta.authRequired) {
    if (!store.state.auth.isUserLoggedIn() && !JSON.parse(sessionStorage.getItem('loggedIn'))) {
      router.push({ path: '/pages/login', query: { to: to.path } })
    }
  }

  return next()
  // Specify the current path as the customState parameter, meaning it
  // will be returned to the application after auth
  // auth.login({ target: to.path });
})

export default router
